/* eslint no-console:0 */
// Vite entrypoint, compiled for the browser and referenced from the layout via
// `<%= vite_javascript_tag 'application' %>`. This replaces the old Webpacker
// pack at app/javascript/packs/application.js.
//
// `window.Routes` (Rails URL helpers, used by the Stimulus controllers) is
// provided globally by the Sprockets pipeline via `//= require js-routes` in
// app/assets/javascripts/application.js, which is loaded on every page through
// the main layout — so it is not re-imported here.

import { captureException } from '@sentry/browser';

// Imported first so Sentry captures errors from the moment the app loads.
import '../sentry';

import 'cleave.js';
import 'cleave.js/dist/addons/cleave-phone.us.js';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';
import { createElement } from 'react';
import { createRoot } from 'react-dom/client';

import '../controllers';
import '../styles/application.sass';

// Exposed as a global so the sprockets-loaded PhoneNumberInput component
// (app/assets/javascripts) can enhance the phone field, mirroring how Cleave
// is consumed.
window.intlTelInput = intlTelInput;

// We mount React "islands" rendered by the `react_component` view helper
// (app/helpers/react_component_helper.rb), which emits placeholder elements:
//   <div data-react-class="navigation/LogoContainer" data-react-props="{...}">
// This replaces the react-rails gem + react_ujs package; the app has no
// server-side rendering, so a small client-side mounter is all that's needed.
// The data-react-class/data-react-props attribute names match what the gem
// emitted, so existing markup conventions and tests are unaffected.
//
// Vite resolves the component modules via `import.meta.glob`, keyed by the name
// used in the views (path under components/, without extension).
//
// The glob is deliberately lazy. Ten components call `translations.init()` at
// module scope and four of them ship their own i18next configuration with a
// private resource set, so evaluating every component up front lets the last one
// re-init the shared i18next singleton and drop everyone else's namespaces —
// which left the login page rendering raw keys such as "signIn". Importing only
// what a page mounts preserves the evaluation semantics of Webpack's old
// `require.context` and keeps components in their own chunks.
const componentLoaders = import.meta.glob([
	'../components/**/*.{js,jsx,ts,tsx}',
	'!../components/**/*.{test,spec}.{js,jsx,ts,tsx}',
	'!../components/**/__tests__/**',
	'!../components/**/__mocks__/**',
	'!../components/**/mocks/**',
]);

// Deliberately not `Object.hasOwn`: that is an ES2022 runtime API, and we do not override Vite's
// default `build.target` (es2020 / chrome87 / firefox78 / safari14). esbuild transforms syntax but
// never polyfills APIs, so `Object.hasOwn` would throw here at module scope on Safari 14-15.3,
// Firefox 78-91 and Chrome 87-92 and take the whole entrypoint - every React island and Stimulus
// controller - down with it.
const hasOwn = (object, key) =>
	Object.prototype.hasOwnProperty.call(object, key); // NOSONAR

const loaders = {};
const indexAliases = {};
Object.entries(componentLoaders).forEach(([filePath, loader]) => {
	const key = filePath
		.replace('../components/', '')
		.replace(/\.(js|jsx|ts|tsx)$/, '');
	loaders[key] = loader;

	// A directory may be referenced by its name alone (e.g. "export-csv"),
	// which resolves to its index module ("export-csv/index"), matching the
	// directory-index resolution the old Webpack require.context provided.
	const dirMatch = /^(.*)\/index$/.exec(key);
	if (dirMatch) indexAliases[dirMatch[1]] = loader;
});
Object.entries(indexAliases).forEach(([name, loader]) => {
	// An explicit file of the same name takes precedence over the alias.
	if (!hasOwn(loaders, name)) loaders[name] = loader;
});

// Components imported so far, keyed the same way as `loaders`.
const loadedModules = {};

const loadModule = async (key) => {
	if (hasOwn(loadedModules, key)) return;
	loadedModules[key] = await loaders[key]();
};

const MOUNTED = Symbol('reactRoot');

const mountLoaded = (nodes) => {
	nodes.forEach((node) => {
		if (node[MOUNTED]) return;

		const module = loadedModules[node.dataset.reactClass];
		if (!module) return;

		const Component = module.default || module;
		const props = node.dataset.reactProps
			? JSON.parse(node.dataset.reactProps)
			: {};

		const reactRoot = createRoot(node);
		node[MOUNTED] = reactRoot;
		reactRoot.render(createElement(Component, props));
	});
};

const mountComponents = (root = document) => {
	const nodes = Array.from(root.querySelectorAll('[data-react-class]')).filter(
		(node) => !node[MOUNTED],
	);

	// A name with no matching module is a typo in the view, not a load failure. Report it per node
	// and carry on with the rest: throwing here would run before anything mounts, so one stale name
	// in a rarely-rendered partial would blank every island on the page. react_ujs mounted the
	// other nodes too, so this keeps the blast radius at the broken component.
	const mountable = [];
	const pending = [];
	nodes.forEach((node) => {
		const name = node.dataset.reactClass;
		if (!hasOwn(loaders, name)) {
			const error = new Error(
				`react_component: no component registered for "${name}"`,
			);
			console.error(error);
			captureException(error);
			return;
		}
		mountable.push(node);
		if (!hasOwn(loadedModules, name) && !pending.includes(name)) {
			pending.push(name);
		}
	});

	if (pending.length === 0) {
		mountLoaded(mountable);
		return Promise.resolve();
	}

	// Imported one at a time in DOM order, matching the order Webpack's
	// synchronous `require` produced, so import-time side effects (i18next
	// initialization in particular) still resolve against each other the same way.
	return pending
		.reduce(
			(chain, key) => chain.then(() => loadModule(key)),
			Promise.resolve(),
		)
		.then(() => mountLoaded(mountable))
		.catch((error) => {
			console.error('react_component: failed to load components', error);
		});
};

// Exposed globally so Sprockets-pipeline scripts (e.g. partner_drawer.es6)
// can re-mount components after injecting HTML via AJAX. Already-mounted nodes
// are skipped, so it is safe to call repeatedly.
window.mountReactComponents = mountComponents;

// The page is destroyed on navigation (no Turbo/Turbolinks/pjax here), so we
// only need to mount on initial load — no unmount handling required.
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => mountComponents());
} else {
	mountComponents();
}
